import { Injectable, signal } from '@angular/core';
import { EmployeeEntity } from '@features/employees';
import { InvestigationEntity } from '../../domain/entities/investigation.entity';
import { InvestigationStatusSnapshotEntity } from '../../domain/entities/investigation-status-snapshot.entity';
import { InvestigationPrioritySnapshotEntity } from '../../domain/entities/investigation-priority-snapshot.entity';
import { InvestigationFieldValueEntity } from '../../domain/entities/investigation-field-value.entity';
import { InvestigationAttachmentEntity } from '../../domain/entities/investigation-attachment.entity';
import { InvestigationInvolvedEmployeeEntity } from '../../domain/entities/investigation-involved-employee.entity';
import { InvestigationWatcherEntity } from '../../domain/entities/investigation-watcher.entity';
import { InvestigationMessageEntity } from '../../domain/entities/investigation-message.entity';
import { InvestigationWorkNoteEntity } from '../../domain/entities/investigation-work-note.entity';
import { InvestigationLinkedInvestigationEntity } from '../../domain/entities/investigation-linked-investigation.entity';
import { InvestigationChecklistStepEntity } from '../../domain/entities/investigation-checklist-step.entity';
import { InvestigationEmailTemplateSnapshotEntity } from '../../domain/entities/investigation-email-template-snapshot.entity';
import { ParentChildInvestigationsEntity } from '../../domain/entities/investigation-parent-child.entity';
import { TransferredCaseFieldValueEntity } from '../../domain/entities/transferred-case-field-value.entity';

@Injectable({ providedIn: 'root' })
export class InvestigationsState {
  private readonly _savingInvestigation = signal(false);
  private readonly _errorInvestigation = signal<string | null>(null);
  private readonly _employees = signal<EmployeeEntity[]>([]);
  private readonly _loadingEmployees = signal(false);
  private readonly _errorEmployees = signal<string | null>(null);
  private readonly _selectedInvestigation = signal<InvestigationEntity | null>(null);
  private readonly _loadingInvestigation = signal(false);
  private readonly _errorLoadingInvestigation = signal<string | null>(null);
  private readonly _statusSnapshots = signal<InvestigationStatusSnapshotEntity[]>([]);
  private readonly _loadingStatusSnapshots = signal(false);
  private readonly _errorStatusSnapshots = signal<string | null>(null);
  private readonly _prioritySnapshots = signal<InvestigationPrioritySnapshotEntity[]>([]);
  private readonly _loadingPrioritySnapshots = signal(false);
  private readonly _errorPrioritySnapshots = signal<string | null>(null);
  private readonly _fieldValues = signal<InvestigationFieldValueEntity[]>([]);
  private readonly _loadingFieldValues = signal(false);
  private readonly _errorFieldValues = signal<string | null>(null);
  private readonly _savingStatus = signal(false);
  private readonly _savingPriority = signal(false);
  private readonly _savingAssignees = signal(false);
  private readonly _savingFieldValues = signal(false);

  public readonly savingInvestigation = this._savingInvestigation.asReadonly();
  public readonly errorInvestigation = this._errorInvestigation.asReadonly();
  public readonly employees = this._employees.asReadonly();
  public readonly loadingEmployees = this._loadingEmployees.asReadonly();
  public readonly errorEmployees = this._errorEmployees.asReadonly();
  public readonly selectedInvestigation = this._selectedInvestigation.asReadonly();
  public readonly loadingInvestigation = this._loadingInvestigation.asReadonly();
  public readonly errorLoadingInvestigation = this._errorLoadingInvestigation.asReadonly();
  public readonly statusSnapshots = this._statusSnapshots.asReadonly();
  public readonly loadingStatusSnapshots = this._loadingStatusSnapshots.asReadonly();
  public readonly errorStatusSnapshots = this._errorStatusSnapshots.asReadonly();
  public readonly prioritySnapshots = this._prioritySnapshots.asReadonly();
  public readonly loadingPrioritySnapshots = this._loadingPrioritySnapshots.asReadonly();
  public readonly errorPrioritySnapshots = this._errorPrioritySnapshots.asReadonly();
  public readonly fieldValues = this._fieldValues.asReadonly();
  public readonly loadingFieldValues = this._loadingFieldValues.asReadonly();
  public readonly errorFieldValues = this._errorFieldValues.asReadonly();
  public readonly savingStatus = this._savingStatus.asReadonly();
  public readonly savingPriority = this._savingPriority.asReadonly();
  public readonly savingAssignees = this._savingAssignees.asReadonly();
  public readonly savingFieldValues = this._savingFieldValues.asReadonly();

  public setSavingInvestigation(saving: boolean): void { this._savingInvestigation.set(saving); }
  public setErrorInvestigation(error: string | null): void { this._errorInvestigation.set(error); }
  public setEmployees(employees: EmployeeEntity[]): void { this._employees.set(employees); }
  public setLoadingEmployees(loading: boolean): void { this._loadingEmployees.set(loading); }
  public setErrorEmployees(error: string | null): void { this._errorEmployees.set(error); }
  public setSelectedInvestigation(investigation: InvestigationEntity | null): void { this._selectedInvestigation.set(investigation); }
  public setLoadingInvestigation(loading: boolean): void { this._loadingInvestigation.set(loading); }
  public setErrorLoadingInvestigation(error: string | null): void { this._errorLoadingInvestigation.set(error); }
  public setStatusSnapshots(snapshots: InvestigationStatusSnapshotEntity[]): void { this._statusSnapshots.set(snapshots); }
  public setLoadingStatusSnapshots(loading: boolean): void { this._loadingStatusSnapshots.set(loading); }
  public setErrorStatusSnapshots(error: string | null): void { this._errorStatusSnapshots.set(error); }
  public setPrioritySnapshots(snapshots: InvestigationPrioritySnapshotEntity[]): void { this._prioritySnapshots.set(snapshots); }
  public setLoadingPrioritySnapshots(loading: boolean): void { this._loadingPrioritySnapshots.set(loading); }
  public setErrorPrioritySnapshots(error: string | null): void { this._errorPrioritySnapshots.set(error); }
  public setFieldValues(fieldValues: InvestigationFieldValueEntity[]): void { this._fieldValues.set(fieldValues); }
  public setLoadingFieldValues(loading: boolean): void { this._loadingFieldValues.set(loading); }
  public setErrorFieldValues(error: string | null): void { this._errorFieldValues.set(error); }
  public setSavingStatus(saving: boolean): void { this._savingStatus.set(saving); }
  public setSavingPriority(saving: boolean): void { this._savingPriority.set(saving); }
  public setSavingAssignees(saving: boolean): void { this._savingAssignees.set(saving); }
  public setSavingFieldValues(saving: boolean): void { this._savingFieldValues.set(saving); }

  // Attachments
  private readonly _attachments = signal<InvestigationAttachmentEntity[]>([]);
  private readonly _loadingAttachments = signal(false);
  private readonly _errorAttachments = signal<string | null>(null);
  public readonly attachments = this._attachments.asReadonly();
  public readonly loadingAttachments = this._loadingAttachments.asReadonly();
  public readonly errorAttachments = this._errorAttachments.asReadonly();
  public setAttachments(attachments: InvestigationAttachmentEntity[]): void { this._attachments.set(attachments); }
  public setLoadingAttachments(loading: boolean): void { this._loadingAttachments.set(loading); }
  public setErrorAttachments(error: string | null): void { this._errorAttachments.set(error); }
  public addAttachment(attachment: InvestigationAttachmentEntity): void { this._attachments.update((list) => [attachment, ...list]); }
  public removeAttachment(id: string): void { this._attachments.update((list) => list.filter((a) => a.id !== id)); }

  private readonly _uploadingAttachment = signal(false);
  private readonly _deletingAttachmentId = signal<string | null>(null);
  public readonly uploadingAttachment = this._uploadingAttachment.asReadonly();
  public readonly deletingAttachmentId = this._deletingAttachmentId.asReadonly();
  public setUploadingAttachment(uploading: boolean): void { this._uploadingAttachment.set(uploading); }
  public setDeletingAttachmentId(id: string | null): void { this._deletingAttachmentId.set(id); }

  // Involved employees
  private readonly _involvedEmployees = signal<InvestigationInvolvedEmployeeEntity[]>([]);
  private readonly _loadingInvolvedEmployees = signal(false);
  private readonly _errorInvolvedEmployees = signal<string | null>(null);
  public readonly involvedEmployees = this._involvedEmployees.asReadonly();
  public readonly loadingInvolvedEmployees = this._loadingInvolvedEmployees.asReadonly();
  public readonly errorInvolvedEmployees = this._errorInvolvedEmployees.asReadonly();
  public setInvolvedEmployees(employees: InvestigationInvolvedEmployeeEntity[]): void { this._involvedEmployees.set(employees); }
  public setLoadingInvolvedEmployees(loading: boolean): void { this._loadingInvolvedEmployees.set(loading); }
  public setErrorInvolvedEmployees(error: string | null): void { this._errorInvolvedEmployees.set(error); }

  // Watchers
  private readonly _watchers = signal<InvestigationWatcherEntity[]>([]);
  private readonly _loadingWatchers = signal(false);
  public readonly watchers = this._watchers.asReadonly();
  public readonly loadingWatchers = this._loadingWatchers.asReadonly();
  public setWatchers(watchers: InvestigationWatcherEntity[]): void { this._watchers.set(watchers); }
  public setLoadingWatchers(loading: boolean): void { this._loadingWatchers.set(loading); }

  // Messages
  private readonly _messages = signal<InvestigationMessageEntity[]>([]);
  private readonly _loadingMessages = signal(false);
  private readonly _errorMessages = signal<string | null>(null);
  private readonly _addingMessage = signal(false);
  public readonly messages = this._messages.asReadonly();
  public readonly loadingMessages = this._loadingMessages.asReadonly();
  public readonly errorMessages = this._errorMessages.asReadonly();
  public readonly addingMessage = this._addingMessage.asReadonly();
  public setMessages(messages: InvestigationMessageEntity[]): void { this._messages.set(messages); }
  public setLoadingMessages(loading: boolean): void { this._loadingMessages.set(loading); }
  public setErrorMessages(error: string | null): void { this._errorMessages.set(error); }
  public setAddingMessage(adding: boolean): void { this._addingMessage.set(adding); }

  // Work Notes
  private readonly _workNotes = signal<InvestigationWorkNoteEntity[]>([]);
  private readonly _loadingWorkNotes = signal(false);
  private readonly _errorWorkNotes = signal<string | null>(null);
  private readonly _addingWorkNote = signal(false);
  public readonly workNotes = this._workNotes.asReadonly();
  public readonly loadingWorkNotes = this._loadingWorkNotes.asReadonly();
  public readonly errorWorkNotes = this._errorWorkNotes.asReadonly();
  public readonly addingWorkNote = this._addingWorkNote.asReadonly();
  public setWorkNotes(workNotes: InvestigationWorkNoteEntity[]): void { this._workNotes.set(workNotes); }
  public setLoadingWorkNotes(loading: boolean): void { this._loadingWorkNotes.set(loading); }
  public setErrorWorkNotes(error: string | null): void { this._errorWorkNotes.set(error); }
  public setAddingWorkNote(adding: boolean): void { this._addingWorkNote.set(adding); }

  // Linked investigations
  private readonly _linkedInvestigations = signal<InvestigationLinkedInvestigationEntity[]>([]);
  private readonly _loadingLinkedInvestigations = signal(false);
  private readonly _errorLinkedInvestigations = signal<string | null>(null);
  private readonly _linkingInvestigation = signal(false);
  private readonly _unlinkingInvestigation = signal(false);
  public readonly linkedInvestigations = this._linkedInvestigations.asReadonly();
  public readonly loadingLinkedInvestigations = this._loadingLinkedInvestigations.asReadonly();
  public readonly errorLinkedInvestigations = this._errorLinkedInvestigations.asReadonly();
  public readonly linkingInvestigation = this._linkingInvestigation.asReadonly();
  public readonly unlinkingInvestigation = this._unlinkingInvestigation.asReadonly();
  public setLinkedInvestigations(items: InvestigationLinkedInvestigationEntity[]): void { this._linkedInvestigations.set(items); }
  public setLoadingLinkedInvestigations(loading: boolean): void { this._loadingLinkedInvestigations.set(loading); }
  public setErrorLinkedInvestigations(error: string | null): void { this._errorLinkedInvestigations.set(error); }
  public setLinkingInvestigation(linking: boolean): void { this._linkingInvestigation.set(linking); }
  public setUnlinkingInvestigation(unlinking: boolean): void { this._unlinkingInvestigation.set(unlinking); }
  public removeLinkedInvestigation(linkedInvestigationId: string): void {
    this._linkedInvestigations.update((items) => items.filter((i) => i.linkedInvestigationId !== linkedInvestigationId));
  }

  // Checklist steps
  private readonly _checklistSteps = signal<InvestigationChecklistStepEntity[]>([]);
  private readonly _loadingChecklistSteps = signal(false);
  private readonly _errorChecklistSteps = signal<string | null>(null);
  private readonly _updatingChecklistStep = signal(false);
  public readonly checklistSteps = this._checklistSteps.asReadonly();
  public readonly loadingChecklistSteps = this._loadingChecklistSteps.asReadonly();
  public readonly errorChecklistSteps = this._errorChecklistSteps.asReadonly();
  public readonly updatingChecklistStep = this._updatingChecklistStep.asReadonly();
  public setChecklistSteps(steps: InvestigationChecklistStepEntity[]): void { this._checklistSteps.set(steps); }
  public setLoadingChecklistSteps(loading: boolean): void { this._loadingChecklistSteps.set(loading); }
  public setErrorChecklistSteps(error: string | null): void { this._errorChecklistSteps.set(error); }
  public setUpdatingChecklistStep(updating: boolean): void { this._updatingChecklistStep.set(updating); }
  public updateChecklistStepCompletion(stepId: string, isCompleted: boolean): void {
    this._checklistSteps.update((steps) =>
      steps.map((s) => s.id === stepId ? { ...s, isCompleted } : s)
    );
  }

  // Parent/child investigations
  private readonly _parentChildInvestigations = signal<ParentChildInvestigationsEntity | null>(null);
  private readonly _loadingParentChildInvestigations = signal(false);
  private readonly _errorParentChildInvestigations = signal<string | null>(null);
  public readonly parentChildInvestigations = this._parentChildInvestigations.asReadonly();
  public readonly loadingParentChildInvestigations = this._loadingParentChildInvestigations.asReadonly();
  public readonly errorParentChildInvestigations = this._errorParentChildInvestigations.asReadonly();
  public setParentChildInvestigations(data: ParentChildInvestigationsEntity | null): void { this._parentChildInvestigations.set(data); }
  public setLoadingParentChildInvestigations(loading: boolean): void { this._loadingParentChildInvestigations.set(loading); }
  public setErrorParentChildInvestigations(error: string | null): void { this._errorParentChildInvestigations.set(error); }

  // Email template snapshots
  private readonly _emailTemplateSnapshots = signal<InvestigationEmailTemplateSnapshotEntity[]>([]);
  private readonly _loadingEmailTemplateSnapshots = signal(false);
  private readonly _errorEmailTemplateSnapshots = signal<string | null>(null);
  public readonly emailTemplateSnapshots = this._emailTemplateSnapshots.asReadonly();
  public readonly loadingEmailTemplateSnapshots = this._loadingEmailTemplateSnapshots.asReadonly();
  public readonly errorEmailTemplateSnapshots = this._errorEmailTemplateSnapshots.asReadonly();
  public setEmailTemplateSnapshots(snapshots: InvestigationEmailTemplateSnapshotEntity[]): void { this._emailTemplateSnapshots.set(snapshots); }
  public setLoadingEmailTemplateSnapshots(loading: boolean): void { this._loadingEmailTemplateSnapshots.set(loading); }
  public setErrorEmailTemplateSnapshots(error: string | null): void { this._errorEmailTemplateSnapshots.set(error); }

  // Transfer-to-ER (HCMI-796) — original case field values, rendered in the read-only
  // "Submission from Shared Services" card while isPendingReclassification is true.
  private readonly _transferredCaseFields = signal<TransferredCaseFieldValueEntity[]>([]);
  private readonly _loadingTransferredCaseFields = signal(false);
  private readonly _errorTransferredCaseFields = signal<string | null>(null);
  private readonly _changingInvestigationType = signal(false);
  public readonly transferredCaseFields = this._transferredCaseFields.asReadonly();
  public readonly loadingTransferredCaseFields = this._loadingTransferredCaseFields.asReadonly();
  public readonly errorTransferredCaseFields = this._errorTransferredCaseFields.asReadonly();
  public readonly changingInvestigationType = this._changingInvestigationType.asReadonly();
  public setTransferredCaseFields(fields: TransferredCaseFieldValueEntity[]): void { this._transferredCaseFields.set(fields); }
  public setLoadingTransferredCaseFields(loading: boolean): void { this._loadingTransferredCaseFields.set(loading); }
  public setErrorTransferredCaseFields(error: string | null): void { this._errorTransferredCaseFields.set(error); }
  public setChangingInvestigationType(changing: boolean): void { this._changingInvestigationType.set(changing); }
}
